import React from 'react';
import PropTypes from 'prop-types';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { WizardFieldGroup, WizardInput, WizardSelect } from './WizardField';
import { ORG_SIZE_OPTIONS, INDUSTRY_OPTIONS } from '../utils/constants';
import { useTranslation } from 'react-i18next';

/**
 * Step 1: Company Information — modern Radix-styled inputs.
 * Organization name is required; size and industry are optional.
 */
const WizardStep1 = ({ formData, updateField, onNext, onSkip, errors }) => {
  const { t } = useTranslation('onboarding');
  const isNextDisabled = !formData.organizationName || formData.organizationName.trim().length < 2;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-800 m-0">
          {t('wizard.companyInfo', 'Company information')}
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          {t('wizard.companyInfoHint', 'A few quick details so we know who you are.')}
        </p>
      </div>

      <WizardFieldGroup
        id="organizationName"
        label={t('wizard.orgName', 'Organization name')}
        required
        error={errors?.organizationName}
      >
        <WizardInput
          id="organizationName"
          value={formData.organizationName}
          onChange={(v) => updateField('organizationName', v)}
          error={errors?.organizationName}
          placeholder={t('wizard.orgNamePlaceholder', 'Acme Corporation')}
          maxLength={100}
          autoFocus
        />
      </WizardFieldGroup>

      <WizardFieldGroup
        id="organizationSize"
        label={t('wizard.orgSize', 'How many people work at your organization?')}
      >
        <WizardSelect
          id="organizationSize"
          value={formData.organizationSize}
          onChange={(v) => updateField('organizationSize', v)}
          options={ORG_SIZE_OPTIONS}
          placeholder={t('wizard.selectSize', 'Select size')}
        />
      </WizardFieldGroup>

      <WizardFieldGroup
        id="industrySector"
        label={t('wizard.industryQuestion', 'What industry best describes your company?')}
      >
        <WizardSelect
          id="industrySector"
          value={formData.industrySector}
          onChange={(v) => updateField('industrySector', v)}
          options={INDUSTRY_OPTIONS}
          placeholder={t('wizard.selectIndustry', 'Select industry')}
        />
      </WizardFieldGroup>

      <div className="flex justify-end gap-2 mt-8 pt-4 border-t border-slate-100">
        <CgRadixButton
          variant="ghost"
          size="lg"
          onClick={onSkip}
          disabled={isNextDisabled}
        >
          {t('actions.skip', 'Skip')}
        </CgRadixButton>
        <CgRadixButton
          variant="primary"
          size="lg"
          onClick={onNext}
          disabled={isNextDisabled}
          icon={faArrowRight}
        >
          {t('common:actions.continue', 'Continue')}
        </CgRadixButton>
      </div>
    </div>
  );
};

WizardStep1.propTypes = {
  formData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default WizardStep1;
